import { useState } from 'react';
import { UFS, type IcmsUfLegalResponse } from '@triade/contracts';
import { formatarTaxa } from '@/lib/format';
import { cn } from '@/lib/utils';
import { type ExcecaoUfEntrada, type PerfilEntrada } from './tabela-form';

const CAMPO = 'h-[30px] w-full rounded-[8px] border border-input bg-card px-2 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const BOTAO = 'h-[30px] rounded-[9px] border border-input bg-card px-2.5 text-caption font-semibold text-ink-body hover:bg-line-soft disabled:opacity-50';
const EXCECAO_CHIP = 'rounded-full bg-primary/10 px-2 py-0.5 text-caption font-semibold text-primary';

const EXCECAO_VAZIA: ExcecaoUfEntrada = { interestadual: '', internaDestino: '', fcp: '', reducaoBase: '', mva: '', observacao: '' };

/** Texto da regra interestadual (7% ou 12%): mostrado como texto "legal", nao e calculado na tela. */
const INTERESTADUAL_LEGAL = 'legal: 7% ou 12% (regra)';

/** Ajuda da coluna MVA (tooltip). */
const AJUDA_MVA = 'MVA original da ST para esse destino; vence a MVA do item.';

export interface GradeIcmsProps {
  perfil: PerfilEntrada;
  onChange: (p: PerfilEntrada) => void;
  podeEditar: boolean;
  /** Tabela legal por UF (icms-legal). Sem ela, as celulas legais ficam em "carregando". */
  legal: IcmsUfLegalResponse[] | undefined;
  legalErro: boolean;
  /** UFs com clientes cadastrados (filtro). Ausente = sem filtro. */
  ufsComClientes?: Set<string>;
}

/** Grade de ICMS por UF: 27 linhas. Legal em cinza; excecao em destaque com "voltar ao legal". */
export function GradeIcms({ perfil, onChange, podeEditar, legal, legalErro, ufsComClientes }: GradeIcmsProps) {
  const porUf = new Map((legal ?? []).map((l) => [l.uf, l]));
  const soComClientes = ufsComClientes !== undefined;
  const [filtrarLigado, setFiltrar] = useState(false);
  const filtrar = soComClientes && filtrarLigado;
  const ufs = UFS.filter((uf) => !filtrar || ufsComClientes?.has(uf));
  const bloqueada = !podeEditar || perfil.icmsFixo;

  const atualizarGrade = (uf: string, parcial: Partial<ExcecaoUfEntrada>) =>
    onChange({ ...perfil, grade: { ...perfil.grade, [uf]: { ...(perfil.grade[uf] ?? EXCECAO_VAZIA), ...parcial } } });

  const criarExcecao = (uf: string) => onChange({ ...perfil, grade: { ...perfil.grade, [uf]: { ...EXCECAO_VAZIA } } });

  const voltarAoLegal = (uf: string) => {
    const { [uf]: _removida, ...resto } = perfil.grade;
    onChange({ ...perfil, grade: resto });
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="flex items-start gap-2 text-small text-ink-body">
        <input
          type="checkbox"
          checked={perfil.icmsFixo}
          disabled={!podeEditar}
          aria-describedby="icms-fixo-aviso"
          onChange={(e) => onChange({ ...perfil, icmsFixo: e.target.checked })}
        />
        <span>
          ICMS fixo em todas as UFs
          <span id="icms-fixo-aviso" className="block text-caption text-ink-muted">
            A linha de ICMS vale para qualquer destino. DIFAL e interestadual não são calculados.
          </span>
        </span>
      </label>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-caption text-ink-muted">
          {legalErro ? 'Não foi possível carregar a tabela legal do ICMS.' : 'Cinza = valor legal (tabela oficial). Destaque = exceção desta tabela.'}
        </span>
        {soComClientes && (
          <label className="flex items-center gap-2 text-small text-ink-body">
            <input type="checkbox" checked={filtrar} disabled={ufsComClientes.size === 0} onChange={(e) => setFiltrar(e.target.checked)} />
            Só UFs com clientes
          </label>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[920px] border-collapse text-small" aria-label="Grade de ICMS por UF">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="py-1.5 font-medium">UF</th>
              <th scope="col" className="py-1.5 font-medium">Interestadual</th>
              <th scope="col" className="py-1.5 font-medium">Interna do destino</th>
              <th scope="col" className="py-1.5 font-medium">FCP</th>
              <th scope="col" className="py-1.5 font-medium">Redução de base</th>
              <th scope="col" className="py-1.5 font-medium" title={AJUDA_MVA}>MVA original (%)</th>
              <th scope="col" className="py-1.5 font-medium">Origem</th>
              <th scope="col" className="py-1.5 font-medium"><span className="sr-only">Ações</span></th>
            </tr>
          </thead>
          <tbody>
            {ufs.map((uf) => {
              const x = perfil.grade[uf];
              const l = porUf.get(uf);
              return (
                <tr key={uf} data-testid={`uf-${uf}`} data-excecao={x ? 'sim' : 'nao'} className={cn('border-t border-line-soft', x && 'bg-primary/5')}>
                  <td className="py-1.5 font-semibold">{uf}</td>
                  <td className="py-1.5 pr-2">
                    {x ? (
                      <input aria-label={`Interestadual ${uf}`} inputMode="decimal" className={CAMPO} placeholder="legal" disabled={bloqueada} value={x.interestadual} onChange={(e) => atualizarGrade(uf, { interestadual: e.target.value })} />
                    ) : (
                      <span className="text-ink-muted">{INTERESTADUAL_LEGAL}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    {x ? (
                      <input aria-label={`Interna do destino ${uf}`} inputMode="decimal" className={CAMPO} placeholder="legal" disabled={bloqueada} value={x.internaDestino} onChange={(e) => atualizarGrade(uf, { internaDestino: e.target.value })} />
                    ) : (
                      <LegalCelula valor={l?.interna ?? null} status={l?.status} carregando={!legal && !legalErro} />
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    {x ? (
                      <input aria-label={`FCP ${uf}`} inputMode="decimal" className={CAMPO} placeholder="legal" disabled={bloqueada} value={x.fcp} onChange={(e) => atualizarGrade(uf, { fcp: e.target.value })} />
                    ) : (
                      <LegalCelula valor={l?.fcp ?? null} carregando={!legal && !legalErro} />
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    {x ? (
                      <input aria-label={`Redução de base ${uf}`} inputMode="decimal" className={CAMPO} placeholder="sem" disabled={bloqueada} value={x.reducaoBase} onChange={(e) => atualizarGrade(uf, { reducaoBase: e.target.value })} />
                    ) : (
                      <span className="text-ink-muted">—</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    <input aria-label={`MVA original ${uf}`} title={AJUDA_MVA} inputMode="decimal" className={CAMPO} placeholder="não informa" disabled={bloqueada} value={x?.mva ?? ''} onChange={(e) => atualizarGrade(uf, { mva: e.target.value })} />
                  </td>
                  <td className="py-1.5">{x ? <span className={EXCECAO_CHIP}>exceção</span> : <span className="text-caption text-ink-muted">legal</span>}</td>
                  <td className="py-1.5 text-right">
                    {x ? (
                      <button type="button" className={BOTAO} disabled={bloqueada} onClick={() => voltarAoLegal(uf)}>
                        Voltar ao legal
                      </button>
                    ) : (
                      <button type="button" className={BOTAO} disabled={bloqueada} onClick={() => criarExcecao(uf)}>
                        Criar exceção
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {Object.keys(perfil.grade).length > 0 && (
        <div className="flex flex-col gap-2">
          {Object.keys(perfil.grade).map((uf) => (
            <label key={uf} className="flex items-center gap-2 text-caption text-ink-secondary">
              <span className="w-8 font-semibold">{uf}</span>
              <span className="sr-only">Observação da exceção {uf}</span>
              <input
                aria-label={`Observação da exceção ${uf}`}
                className={cn(CAMPO, 'max-w-[520px]')}
                maxLength={300}
                placeholder="Motivo da exceção (ex.: benefício fiscal)"
                disabled={bloqueada}
                value={perfil.grade[uf]?.observacao ?? ''}
                onChange={(e) => atualizarGrade(uf, { observacao: e.target.value })}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function LegalCelula({ valor, status, carregando }: { valor: string | null; status?: IcmsUfLegalResponse['status']; carregando: boolean }) {
  if (carregando) return <span className="text-ink-muted">…</span>;
  if (valor === null) return <span className="text-caption text-ink-muted">alíquota interna não cadastrada</span>;
  return (
    <span className="tabular text-ink-muted">
      {formatarTaxa(valor)}
      {status === 'A_CONFIRMAR' && <span className="ml-1 text-caption">(a confirmar)</span>}
    </span>
  );
}

